import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Download, 
  Trash2, 
  Smartphone, 
  EyeOff, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  Scissors
} from 'lucide-react';
import VideoPlayer from '../VideoPlayer';
import VideoEditor, { VideoCaption } from '../VideoCaptionsManager';
import { cn } from "../../lib/utils";

// shadcn UI
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import { Label } from "../ui/label";
import { toast } from "sonner";

import { API_BASE_URL } from '../../config';

interface VideoTrimmerProps {
  videoUrl: string;
  videoDuration: number;
  onStartTimeChange: (startTime: number) => void;
  onEndTimeChange: (endTime: number) => void;
  maxDuration?: number;
  addEndLogo?: boolean;
  mediaPath?: string;
  convertTo60FPS?: boolean;
  localFile?: File | null;
  onDelete?: () => void;
  needsMobileFormat?: boolean;
  cropData?: { x: number; y: number; width: number; height: number; aspect: number } | null;
  onVideoDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  onCaptionsChange?: (captions: VideoCaption[]) => void;
  initialCaptions?: VideoCaption[];
  openCaptionsManager?: boolean;
  onCaptionsManagerClose?: () => void;
  initialStartTime?: number;
  initialEndTime?: number;
  crop?: { x: number; y: number } | null;
  zoom?: number | null;
  onCropChange?: (cropData: any, crop: { x: number; y: number } | null, zoom: number) => void;
}

export default function VideoTrimmer({
  videoUrl,
  videoDuration,
  onStartTimeChange,
  onEndTimeChange,
  maxDuration = 140,
  addEndLogo = false,
  mediaPath,
  convertTo60FPS = true,
  localFile,
  onDelete,
  needsMobileFormat = false,
  cropData = null,
  onVideoDimensionsChange,
  onCaptionsChange,
  initialCaptions = [],
  openCaptionsManager = false,
  onCaptionsManagerClose,
  initialStartTime = 0,
  initialEndTime,
  crop: externalCrop = null,
  zoom: externalZoom = 1,
  onCropChange,
}: VideoTrimmerProps): JSX.Element {
  const [startTime, setStartTime] = useState<number>(initialStartTime);
  const [endTime, setEndTime] = useState<number>(initialEndTime ?? videoDuration);
  const token = localStorage.getItem('token');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [downloadReady, setDownloadReady] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastProcessedOptionsRef = useRef<any>(null);
  const [captions, setCaptions] = useState<VideoCaption[]>(initialCaptions);
  const [showCaptionsManager, setShowCaptionsManager] = useState<boolean>(false);
  const [showStoryOverlay, setShowStoryOverlay] = useState<boolean>(false);
  const [crop, setCrop] = useState<{ x: number; y: number } | null>(externalCrop);
  const [zoom, setZoom] = useState<number | null>(externalZoom);
  
  useEffect(() => {
    if (externalCrop) setCrop(externalCrop);
    if (externalZoom) setZoom(externalZoom);
  }, [externalCrop, externalZoom]);
  
  useEffect(() => {
    if (openCaptionsManager !== showCaptionsManager) setShowCaptionsManager(openCaptionsManager);
  }, [openCaptionsManager]);
  
  useEffect(() => { setCaptions(initialCaptions); }, [initialCaptions]);

  useEffect(() => {
    if (videoDimensions && onVideoDimensionsChange) onVideoDimensionsChange(videoDimensions);
  }, [videoDimensions]);

  useEffect(() => {
    if (videoDuration > 0) {
      const newStartTime = initialStartTime ?? 0;
      const newEndTime = initialEndTime ?? videoDuration;
      setStartTime(newStartTime);
      setEndTime(newEndTime);
      onStartTimeChange(newStartTime);
      onEndTimeChange(newEndTime);
    }
  }, [videoDuration, initialStartTime, initialEndTime]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCaptionsChange = (newCaptions: VideoCaption[]) => {
    setCaptions(newCaptions);
    onCaptionsChange?.(newCaptions);
  };

  const END_LOGO_DURATION = 9;
  const effectiveMaxDuration = addEndLogo ? Math.max(0, maxDuration - END_LOGO_DURATION) : maxDuration;
  const selectedDuration = endTime - startTime;
  const isDurationExceeded = selectedDuration > effectiveMaxDuration;

  async function handleDownload() {
    let finalMediaPath = mediaPath;
    if (localFile && !mediaPath) {
      setProcessing(true);
      setProcessingProgress(0);
      setProcessingStatus('Uploading file...');
      try {
        const formData = new FormData();
        formData.append('media', localFile);
        const res = await axios.post(`${API_BASE_URL}/social/upload-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
        });
        finalMediaPath = res.data.filePath;
      } catch (err: any) {
        setProcessing(false);
        setProcessingError(err.message);
        return;
      }
    }
    
    if (!finalMediaPath) return;

    setProcessing(true);
    setProcessingProgress(0);
    setProcessingStatus('Starting processing...');
    setProcessingError(null);
    setDownloadReady(false);

    try {
      const res = await axios.post(`${API_BASE_URL}/social/process-video`, {
        mediaPath: finalMediaPath,
        startTime: startTime > 0 ? startTime : undefined,
        endTime: endTime < videoDuration ? endTime : undefined,
        convertTo60FPS,
        addEndLogo,
        captions: captions.length > 0 ? captions : undefined,
        cropData: cropData || undefined,
        zoom: zoom || undefined,
        crop: crop || undefined,
      }, { headers: { 'Authorization': `Bearer ${token}` } });

      const newSessionId = res.data.sessionId;
      setSessionId(newSessionId);

      const es = new EventSource(`${API_BASE_URL}/social/process-progress/${newSessionId}?token=${token}`);
      eventSourceRef.current = es;

      es.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.progress !== undefined) setProcessingProgress(data.progress);
        if (data.message) setProcessingStatus(data.message);
        if (data.status === 'completed') {
          es.close();
          // Download the file via fetch + blob to avoid popup blockers
          setProcessingStatus('Downloading...');
          setProcessingProgress(100);
          try {
            const response = await fetch(`${API_BASE_URL}/social/download-video?sessionId=${newSessionId}&token=${token}`);
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `processed_video_${Date.now()}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Video downloaded!");
          } catch (err: any) {
            toast.error("Download failed: " + (err.message || 'Unknown error'));
          }
          setProcessing(false);
          setDownloadReady(true);
        } else if (data.status === 'error') {
          setProcessing(false);
          setProcessingError(data.message);
          es.close();
        }
      };
    } catch (err: any) {
      setProcessing(false);
      setProcessingError(err.message);
    }
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      <VideoPlayer
        videoUrl={videoUrl}
        videoDuration={videoDuration}
        enableTrimming={false}
        startTime={startTime}
        endTime={endTime}
        isPlaying={isPlaying}
        onPlayingChange={setIsPlaying}
        currentTime={currentTime}
        onCurrentTimeChange={setCurrentTime}
        onSeek={setCurrentTime}
        captions={captions}
        needsMobileFormat={needsMobileFormat}
        cropData={cropData}
        showStoryOverlay={showStoryOverlay}
        onVideoDimensionsChange={setVideoDimensions}
        showSlider={true}
        showTrimmer={false}
        crop={crop}
        zoom={zoom}
        onCropChange={onCropChange}
      />

      <div className="bg-background rounded-3xl border border-border p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-1">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Selection Start</Label>
            <p className="text-lg font-black text-foreground">{formatTime(startTime)}</p>
          </div>
          
          <div className={cn(
            "space-y-1 text-center py-3 rounded-2xl border transition-all duration-300",
            isDurationExceeded 
              ? "bg-red-500/10 border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.1)]" 
              : "bg-muted/50 border-border"
          )}>
            <Label className={cn(
              "text-[10px] font-black uppercase tracking-widest transition-colors",
              isDurationExceeded ? "text-red-400" : "text-muted-foreground"
            )}>Active Range</Label>
            <div className="flex items-center justify-center gap-2">
              <span className={cn(
                "text-lg font-black tracking-tight transition-colors",
                isDurationExceeded ? "text-red-500" : "text-primary"
              )}>
                {formatTime(selectedDuration)}
              </span>
              <span className={cn(
                "text-xs font-bold transition-colors",
                isDurationExceeded ? "text-red-400/50" : "text-muted-foreground"
              )}>/ {formatTime(effectiveMaxDuration)}</span>
            </div>
            {isDurationExceeded && (
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <AlertTriangle className="h-2.5 w-2.5 text-red-500" />
                <p className="text-[9px] text-red-400 font-black uppercase animate-pulse">Too long for platform</p>
              </div>
            )}
          </div>

          <div className="space-y-1 text-right">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Selection End</Label>
            <p className="text-lg font-black text-foreground">{formatTime(endTime)}</p>
          </div>
        </div>

        <Separator className="bg-card" />

        <div className="flex items-center justify-between">
          <div className="flex items-center bg-card rounded-full border border-border p-1">
            {needsMobileFormat && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setShowStoryOverlay(!showStoryOverlay)}
                      className={cn(
                        "h-9 w-9 rounded-full transition-colors",
                        showStoryOverlay ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {showStoryOverlay ? <Smartphone className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-muted border-border text-xs">Safe Areas</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleDownload}
                    disabled={processing}
                    className="h-9 w-9 rounded-full text-primary hover:bg-primary/10"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-muted border-border text-xs">Export Video</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {onDelete && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={onDelete}
                      disabled={processing}
                      className="h-9 w-9 rounded-full text-red-500/50 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-muted border-border text-xs text-red-400">Discard Media</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {processing && (
            <div className="flex-1 max-w-[200px] ml-6">
              <p className="text-[9px] font-black text-muted-foreground uppercase truncate text-center">
                Processing...
              </p>
            </div>
          )}
        </div>
      </div>

      {processingError && (
        <Alert variant="destructive" className="bg-red-500/5 border-red-500/20 text-red-400">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-[10px] font-black uppercase">Engine Error</AlertTitle>
          <AlertDescription className="text-xs italic">{processingError}</AlertDescription>
        </Alert>
      )}

      <Dialog open={processing} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[340px] bg-card border-border [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <p className="text-sm font-bold text-foreground">Processing video...</p>
              <p className="text-xs text-muted-foreground">{processingStatus}</p>
            </div>
            <Progress value={processingProgress} className="h-2 w-full bg-card [&>div]:bg-primary" />
            <p className="text-xs font-bold text-muted-foreground">{processingProgress}%</p>
          </div>
        </DialogContent>
      </Dialog>

      {showCaptionsManager && (
        <VideoEditor
          open={showCaptionsManager}
          onClose={() => {
            setShowCaptionsManager(false);
            onCaptionsManagerClose?.();
          }}
          captions={captions}
          onCaptionsChange={handleCaptionsChange}
          videoDuration={videoDuration}
          currentTime={currentTime}
          onSeek={setCurrentTime}
          videoUrl={videoUrl}
          startTime={startTime}
          endTime={endTime}
        />
      )}
    </div>
  );
}
